import { Injectable } from '@angular/core';
import { StorageLoader } from './storageLoader';
import { THEME } from '@constants/app.constants';

export type AppearanceMode = 'system' | 'light' | 'dark';

const APPEARANCE_MIRROR_KEY = 'CENTELHA_APPEARANCE';

function normalizeAppearance(value: unknown): AppearanceMode | null {
  return value === 'light' || value === 'dark' || value === 'system'
    ? value
    : null;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeLoader {

  constructor(
    private storageLoader: StorageLoader
  ) { }

  async initialize() {
    const saved = await this.getAppearance();
    this.apply(saved);

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const syncSystemTheme = async () => {
      const current = await this.getAppearance();

      if (current === 'system') {
        this.apply('system');
      }
    };

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', syncSystemTheme);
    } else if (typeof media.addListener === 'function') {
      media.addListener(syncSystemTheme);
    }
  }

  async setAppearance(mode: AppearanceMode) {
    const next = normalizeAppearance(mode) ?? 'system';
    this.writeAppearanceMirror(next);
    this.apply(next);
    await this.storageLoader.setTheme(next);
  }

  async getAppearance(): Promise<AppearanceMode> {
    const stored = normalizeAppearance(await this.storageLoader.getTheme());

    if (stored) {
      this.writeAppearanceMirror(stored);
      return stored;
    }

    return this.readAppearanceMirror() ?? 'system';
  }

  apply(mode: AppearanceMode) {
    const resolved = this.resolve(mode);
    const root = document.documentElement;
    const body = document.body;

    root.classList.toggle('dark', resolved === 'dark');
    root.classList.toggle('light', resolved === 'light');
    root.dataset['appearance'] = mode;
    root.dataset['theme'] = resolved;

    if (body) {
      body.classList.toggle('dark', resolved === 'dark');
      body.classList.toggle('light', resolved === 'light');
    }
  }

  private resolve(mode: AppearanceMode): 'light' | 'dark' {
    if (mode === 'dark') return 'dark';
    if (mode === 'light') return 'light';

    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }

  private readAppearanceMirror(): AppearanceMode | null {
    try {
      return (
        normalizeAppearance(localStorage.getItem(APPEARANCE_MIRROR_KEY)) ??
        normalizeAppearance(localStorage.getItem(THEME))
      );
    } catch {
      return null;
    }
  }

  private writeAppearanceMirror(mode: AppearanceMode): void {
    try {
      localStorage.setItem(APPEARANCE_MIRROR_KEY, mode);
      localStorage.setItem(THEME, mode);
    } catch {}
  }
}
